import { formatClock, formatDayKey } from '../../utils/dayKey'
import { formatTRY } from '../../utils/money'
import { DEFAULT_RECEIPT, RECEIPT_BRAND, type ReceiptSettings } from '../../types'
import './ReceiptPreview.css'

interface Props {
  settings: ReceiptSettings
  tenantName: string
  /** Which slip's defaults an unset field falls back to. */
  defaults?: Required<Omit<ReceiptSettings, 'title' | 'skipStations' | 'subtitle'>>
}

/** Sample lines, priced so the money rows have something real to add up. */
const SAMPLE = [
  { qty: 2, name: 'Adana Dürüm', note: 'Soğansız', lineTotalKurus: 24000 },
  { qty: 1, name: 'Kuzu Şiş', note: null, lineTotalKurus: 18000 },
  { qty: 3, name: 'Kelle Paça Çorbası', note: null, lineTotalKurus: 13500 },
]
const SAMPLE_TOTAL = SAMPLE.reduce((s, l) => s + l.lineTotalKurus, 0)

/**
 * What the slip will look like, drawn on screen at roughly its printed
 * proportions.
 *
 * Deliberately a separate component from Receipt rather than a reuse of it: the
 * real one lives behind `@media print` and is invisible on screen by design, so
 * showing it here would mean unpicking the very rule that keeps it off the
 * till. This mirrors its structure against fixed sample data instead, the
 * point is to answer "do I want this line" before paper is spent finding out.
 */
export function ReceiptPreview({ settings, tenantName, defaults = DEFAULT_RECEIPT }: Props) {
  const cfg = {
    title: settings.title ?? '',
    footer: settings.footer ?? defaults.footer,
    showReceiptNo: settings.showReceiptNo ?? defaults.showReceiptNo,
    showDate: settings.showDate ?? defaults.showDate,
    showTable: settings.showTable ?? defaults.showTable,
    showWaiter: settings.showWaiter ?? defaults.showWaiter,
    linePercent: settings.linePercent ?? defaults.linePercent,
    fontPercent: settings.fontPercent ?? defaults.fontPercent,
    feedMm: settings.feedMm ?? defaults.feedMm,
    ruleWidthPx: settings.ruleWidthPx ?? defaults.ruleWidthPx,
    ruleStyle: settings.ruleStyle ?? defaults.ruleStyle,
    boxStation: settings.boxStation ?? defaults.boxStation,
    showStationTotal: settings.showStationTotal ?? defaults.showStationTotal,
    showFooterStation: settings.showFooterStation ?? defaults.showFooterStation,
    showPrices: settings.showPrices ?? defaults.showPrices,
  }

  const today = new Date()
  const dayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`

  return (
    <div className="rprev">
      <div
        className="rprev__paper"
        style={
          {
            '--p-line': cfg.linePercent / 100,
            '--p-font': cfg.fontPercent / 100,
            '--p-rule-w': `${cfg.ruleWidthPx}px`,
            '--p-rule-s': cfg.ruleStyle,
            '--p-feed': `${cfg.feedMm}mm`,
          } as React.CSSProperties
        }
      >
        <div className="rprev__head">
          <div className="rprev__title">{cfg.title || tenantName}</div>
          <div className="rprev__sub">{RECEIPT_BRAND}</div>
        </div>

        <hr />

        {cfg.showReceiptNo && (
          <div className="rprev__row">
            <span>Fiş No</span>
            <span>K1-0042</span>
          </div>
        )}
        {cfg.showTable && (
          <div className="rprev__row">
            <span>Masa</span>
            <span>Masa 3</span>
          </div>
        )}
        {cfg.showWaiter && (
          <div className="rprev__row">
            <span>Garson</span>
            <span>Ali Yılmaz</span>
          </div>
        )}
        {cfg.showDate && (
          <div className="rprev__row">
            <span>Tarih</span>
            <span>
              {formatDayKey(dayKey)} {formatClock(today.getTime())}
            </span>
          </div>
        )}

        <hr />

        {/* Sample lines, one of them noted, so the effect of tighter spacing on
            a real ticket is visible rather than guessed at. */}
        {SAMPLE.map((l) => (
          <div key={l.name}>
            <div className="rprev__line">
              <span className="rprev__qty">{l.qty}</span>
              <span className="rprev__name">{l.name}</span>
              {cfg.showPrices && <span>{formatTRY(l.lineTotalKurus)}</span>}
            </div>
            {l.note && <div className="rprev__note">» {l.note}</div>}
          </div>
        ))}

        <hr />

        {cfg.showStationTotal && (
          <div className="rprev__row rprev__row--bold">
            <span>Mutfak</span>
            <span>6 ürün</span>
          </div>
        )}

        {cfg.showPrices && (
          <div className="rprev__row rprev__row--bold">
            <span>TOPLAM</span>
            <span>{formatTRY(SAMPLE_TOTAL)}</span>
          </div>
        )}

        {cfg.showPrices && <hr />}

        <div className="rprev__foot">
          {cfg.showFooterStation && (
            <div>Mutfak</div>
          )}
          {cfg.footer && <div>{cfg.footer}</div>}
        </div>

        {/* The blank tail the printer feeds so the last line clears the tear
            bar. It is paper too, which is why it is adjustable. */}
        <div className="rprev__feed" />
      </div>
    </div>
  )
}
